import { Component, ErrorInfo, ReactNode } from "react";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
}

const AtlantisFallback = () => (
  <div className="min-h-screen bg-background text-foreground">
    <main className="mx-auto max-w-4xl px-6 py-16 md:py-24">
      <p className="mb-4 text-sm uppercase tracking-[0.18em] text-muted-foreground">
        Plentiful Views · Atlantis · San Juan, Puerto Rico
      </p>
      <h1 className="font-serif text-4xl md:text-6xl">Plentiful Views Atlantis</h1>
      <p className="mt-6 text-lg leading-8 text-muted-foreground">
        A two-bedroom, two-bathroom ocean-view vacation rental for up to four guests in San Juan,
        positioned between Old San Juan and Condado with Playa Escambrón nearby.
      </p>

      <section className="mt-12">
        <h2 className="font-serif text-3xl">The residence</h2>
        <p className="mt-4 leading-7 text-muted-foreground">
          Atlantis includes ocean views, a full kitchen, in-unit washer and dryer, high-speed fiber
          Wi-Fi, zoned air conditioning, a dedicated workspace and secure covered parking.
        </p>
      </section>

      <section className="mt-10">
        <h2 className="font-serif text-3xl">Stay between Old San Juan and Condado</h2>
        <p className="mt-4 leading-7 text-muted-foreground">
          Playa Escambrón is roughly half a mile from the residence, while Old San Juan is about a
          mile away. The location makes it easy to combine beach time, historic San Juan, dining and
          short rideshares without building the entire trip around a rental car.
        </p>
      </section>

      <section className="mt-10">
        <h2 className="font-serif text-3xl">Check availability</h2>
        <p className="mt-4 leading-7 text-muted-foreground">
          Live pricing, availability and reservations are handled through Airbnb and VRBO.
        </p>
        <ul className="mt-5 space-y-3">
          <li>
            <a className="underline" href="https://www.airbnb.com/rooms/31145672">
              Check Atlantis availability on Airbnb
            </a>
          </li>
          <li>
            <a className="underline" href="https://www.vrbo.com/1700932">
              Check Atlantis availability on VRBO
            </a>
          </li>
        </ul>
      </section>

      <section className="mt-10">
        <h2 className="font-serif text-3xl">Plan your San Juan stay</h2>
        <ul className="mt-5 space-y-3">
          <li><a className="underline" href="/guides/where-to-stay-in-san-juan">Where to stay in San Juan</a></li>
          <li><a className="underline" href="/guides/escambron-beach-san-juan">Playa Escambrón guide</a></li>
          <li><a className="underline" href="/guides/san-juan-before-after-cruise">San Juan before or after a cruise</a></li>
        </ul>
      </section>
    </main>
  </div>
);

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("ErrorBoundary caught:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback;

      if (typeof window !== "undefined" && window.location.pathname === "/collection/atlantis") {
        return <AtlantisFallback />;
      }

      return (
        <div className="min-h-screen flex items-center justify-center bg-background">
          <div className="text-center px-6">
            <h1 className="font-serif text-3xl text-foreground mb-4">Something went wrong</h1>
            <p className="text-muted-foreground mb-6">Please try refreshing the page.</p>
            <button
              onClick={() => window.location.reload()}
              className="px-6 py-3 bg-primary text-primary-foreground rounded-md"
            >
              Refresh Page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
